import { useState, useRef, useEffect } from "react";
import { useClient } from "../context/ClientContext";
import { CaretDown, Buildings, Check, Lock } from "@phosphor-icons/react";

export default function ClientSwitcher() {
  const { clients, activeClient, setActiveClient } = useClient();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function close(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  if (!activeClient) return null;

  // Single-client view: render as a read-only badge (no dropdown)
  const singleClient = clients.length <= 1;

  if (singleClient) {
    return (
      <div data-testid="client-badge" className="w-full flex items-center gap-2 px-3 py-2 border border-zinc-200 bg-zinc-50 rounded-sm">
        <div className="w-7 h-7 bg-vlink-50 border border-vlink-200 flex items-center justify-center flex-shrink-0">
          <Buildings size={13} className="text-vlink-700" weight="bold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-semibold">Tenant</div>
          <div className="text-sm font-medium text-zinc-900 truncate">{activeClient.name}</div>
          <div className="text-[10px] font-mono text-zinc-500 truncate">{activeClient.platform}</div>
        </div>
        <Lock size={11} weight="bold" className="text-zinc-400" />
      </div>
    );
  }

  return (
    <div ref={ref} className="relative" data-testid="client-switcher">
      <button
        data-testid="client-switcher-trigger"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 border border-zinc-200 hover:border-vlink-500 bg-white text-left transition-colors group rounded-sm"
      >
        <div className="w-7 h-7 bg-vlink-50 border border-vlink-200 flex items-center justify-center flex-shrink-0">
          <Buildings size={13} className="text-vlink-700" weight="bold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-semibold">Active client</div>
          <div className="text-sm font-medium text-zinc-900 truncate">{activeClient.name}</div>
          <div className="text-[10px] font-mono text-zinc-500 truncate">{activeClient.platform}</div>
        </div>
        <CaretDown size={12} className="text-zinc-400 group-hover:text-zinc-700 transition" />
      </button>

      {open && (
        <div
          data-testid="client-switcher-menu"
          className="absolute z-50 left-0 right-0 mt-1 bg-white border border-zinc-200 shadow-md max-h-80 overflow-y-auto"
        >
          <div className="px-3 py-2 overline text-zinc-500 border-b border-zinc-100 bg-zinc-50">
            Switch client ({clients.length})
          </div>
          {clients.map((c) => {
            const active = c.id === activeClient.id;
            return (
              <button
                key={c.id}
                data-testid={`client-option-${c.id}`}
                onClick={() => { setActiveClient(c.id); setOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm border-b border-zinc-50 last:border-0 hover:bg-vlink-50 transition-colors ${
                  active ? "bg-vlink-50" : ""
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-zinc-900 flex items-center gap-2">
                    {c.name}
                    {active && <Check size={12} className="text-vlink-600" weight="bold" />}
                  </div>
                  <div className="text-[11px] text-zinc-500 font-mono">{c.platform} · {c.sector}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-xs font-mono text-zinc-700">{c.rcs}</div>
                  <div className={`text-[9px] uppercase tracking-wider font-semibold ${
                    c.rcs_band === "Recommended" ? "text-emerald-700"
                    : c.rcs_band === "Hold" ? "text-rose-700" : "text-amber-700"
                  }`}>{c.rcs_band}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
